<template>
  <div class="rep-search">
    <div class="flex-row gap-x-2">
      <div class="flex-static rep-search-width">
        <p class="m-no-t m-sm-b">
          Search by any of the criteria below.
          <br />One or more fields may be used.
        </p>
        <PapaAutoForm
          v-model="formData"
          :fields="[
            { name: 'rep_number', label: 'Consultant Number' },
            { name: 'first_name', label: 'Consultant First Name' },
            { name: 'last_name', label: 'Consultant Last Name' },
            { name: 'zip_code', label: 'Zip Code' },
          ]"
          sub
        />
      </div>
      <div class="flex-stretch">
        <div class="alert alert-danger" v-if="context.error">
          {{ context.error }}
        </div>
        <Message
          v-if="!context.status && hasSearchParams && activeReps.length == 0"
          severity="error"
          :closable="false"
        >
          No Consultants match your search. Please review your search criteria
          and try again.
        </Message>
        <div class="reps-container" v-else-if="activeReps.length > 0">
          <p class="m-no-t m-sm-b">Here are the Consultants we found for you</p>

          <dialog
            v-if="context.rep"
            @click="closeDialog"
            ref="selected"
            class="rep-detail"
          >
            <div class="flex-column gap-md">
              <div class="flex-row flex-center gap-x-2">
                <div class="main">
                  <ThumbnailedImage
                    v-if="context.rep?.images"
                    :images="context.rep.images"
                    :alt="context.rep.name ?? 'Consultant'"
                  />
                  <img
                    v-else
                    :src="context.fallbackImage"
                    :alt="context.rep.name"
                  />
                </div>
                <div class="flex-column flex-center gap-2xs rep-info">
                  <div class="title">{{ context.rep.name }}</div>
                  <div class="title">
                    {{ context.rep.rep_number }}
                  </div>
                  <div v-if="context.rep.email" class="email">
                    <a target="_blank" :href="`mailto: ${context.rep.email}`">{{
                      context.rep.email
                    }}</a>
                  </div>
                </div>
              </div>

              <div class="bio">{{ context.rep.bio }}</div>

              <div class="buttons flex-row flex-center">
                <Button
                  @click.prevent="chooseConsultant()"
                  class="choose-consultant grey"
                  aria-label="Choose Consultant"
                >
                  Choose Consultant
                </Button>
              </div>
            </div>
          </dialog>

          <div class="reps gap-sm">
            <a
              href="#"
              v-for="object of activeReps"
              :key="object.id"
              :class="{ active: context.rep && object.id == context.rep.id }"
              @click.prevent="handleSelect($event, object)"
              class="consultant-icon"
              :aria-label="`Choose ${object.rep_number}`"
            >
              <ThumbnailedImage
                v-if="object.images"
                :images="object.images"
                :alt="object.name ?? 'Consultant'"
              />
              <img
                v-else
                :src="context.fallbackImage"
                :alt="object.name ?? 'Consultant'"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "@/utils/bouncer";
import type { Rep } from "@paparazzi/types/paparazzi.api.serializers.profiles";
import api from "@virgodev/bazaar/functions/api";
import { useLocalStorageStore } from "@virgodev/bazaar/functions/localstorage/store";
import Button from "primevue/button";
import Message from "primevue/message";
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import PapaAutoForm from "../PapaAutoForm.vue";
import ThumbnailedImage from "../ThumbnailedImage.vue";

const props = defineProps<{
  modelValue?: Rep | null;
  initialRepNumber?: string;
}>();
const emits = defineEmits(["update:modelValue"]);

const storage = useLocalStorageStore();

const selected = ref();
const context = reactive({
  submitId: 1,
  error: null,
  distance: 10,
  geolocate: new Boolean(navigator.geolocation),
  rep: null as Rep | null,
  reps: [] as Rep[],
  status: "",
  fallbackImage:
    "https://paparazziaccessories.com/media/static/site/images/paparazzi-consultant-logo-150.png",
});

const formData = ref({
  first_name: "",
  last_name: "",
  rep_number: "",
  zip_code: storage.get("findConsultantZip", ""),
} as {
  first_name?: string;
  last_name?: string;
  rep_number?: string;
  zip_code?: string;
});

const activeReps = computed(() => {
  let reps = context.reps;

  // Filter out paparazzi accounts if not tester
  // filter out a bug where undefined was set to tester as a string
  // can remove this after the site goes live
  if (!localStorage.tester || localStorage.tester === "undefined") {
    reps = reps.filter((r) => {
      const retval =
        `${r.rep_number}` === formData.value.rep_number ||
        !r.email?.includes("@paparazziaccessories.com");
      return retval;
    });
  }

  return reps.filter((r) => r.is_active);
});
const hasSearchParams = computed(() => {
  return Object.values(formData.value).filter((a) => a).length > 0;
});

watch(
  () => formData.value.zip_code,
  () => {
    if (formData.value.zip_code !== null) {
      storage.put("findConsultantZip", formData.value.zip_code);
    }
  },
);
watch(formData, () => {
  handleSubmit();
});

onMounted(() => {
  if (props.initialRepNumber) {
    formData.value = { rep_number: props.initialRepNumber };
    handleSubmit();
  } else if (formData.value.zip_code) {
    handleSubmit();
  }
});

function loadImage(src: string) {
  if (import.meta.env.VITEST) {
    return;
  }
  return new Promise((resolve, reject) => {
    let i = new Image();
    i.onload = () => {
      resolve(src);
    };
    i.onerror = () => {
      resolve(context.fallbackImage);
    };
    i.src = src;
  });
}

async function handleSubmit(preload = true) {
  context.status = "loading";
  if (await debounce("find-consultant", 500)) {
    let submitId = ++context.submitId;
    context.error = null;
    context.rep = null;
    let data = {
      distance: 10,
      search_by: "zip_code",
      ...formData.value,
    };
    let response = await api({ url: "profile/nearby/", params: data });
    let fallbackLoaded = loadImage(context.fallbackImage);
    if (context.submitId == submitId) {
      context.reps = [];
    }
    if (response.ok) {
      if (preload) {
        await Promise.all(
          response.body.map(async (rep: Rep) => {
            if (rep.images?.cropped_100) {
              await loadImage(rep.images.cropped_100);
            } else {
              await fallbackLoaded;
            }
            if (context.submitId == submitId) {
              const index = Math.floor(Math.random() * context.reps.length);
              context.reps.splice(index, 0, rep);
            }
          }),
        );
      } else {
        if (context.submitId == submitId) {
          context.reps = response.body;
        }
      }
    }
    context.status = "";
  }
}

async function handleSelect(e: MouseEvent, object: Rep) {
  if (object.is_active !== false) {
    if (context.rep == object) {
      context.rep = null;
    } else {
      context.rep = object;
      await nextTick();
      if (selected.value.showModal) {
        selected.value.showModal();
      }
    }
  }
}

async function closeDialog(e: MouseEvent) {
  if (e.target === selected.value) {
    selected.value.close();
    context.rep = null;
  }
}

function chooseConsultant() {
  if (context.rep) {
    emits("update:modelValue", context.rep);
    if (typeof selected.value?.close === "function") {
      selected.value.close();
    }
  }
}
</script>

<style scoped>
.reps {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: repeat(3, minmax(99px, 100px));
}
.reps img {
  display: flex;
  flex-direction: row;
  min-width: 100px;
}
.close {
  float: right;
}

::backdrop {
  background-image: linear-gradient(90deg, #333, #000);
  opacity: 0.5;
}
dialog {
  width: min(100vw, 650px);
}
dialog img {
  width: 160px;
  height: 160px;
}
.rep-search-width {
  min-width: 356px;
}
.reps-container {
}
.rep-detail {
  padding: var(--3XL, 80px) var(--4-xl, 126px);
}
.rep-info {
  width: 130px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
.p-button {
  width: fit-content;
  margin: 0 auto;
}
@media (max-width: 945px) {
  .rep-search:deep(.flex-row) {
    flex-direction: column !important;
  }
  .reps-container {
    margin-top: var(--2XL, 72px);
  }
  .rep-search:deep(.flex-row.flex-center.gap-x-2) {
    flex-direction: row !important;
  }
}

@media (max-width: 800px) {
  dialog {
    width: min(100vw, 390px);
  }
  .reps-container {
  }
  .rep-detail {
    padding: var(--Sm, 24px);
  }
  .rep-info {
    width: 130px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    .title.text-nowrap,
    .email.text-nowrap {
      white-space: revert-layer !important;
    }
  }
  .reps {
    justify-content: center;
  }
}
</style>
